import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { Users, Repeat, UserPlus, Heart, Sparkles, ShieldAlert, TrendingUp } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { formatDate } from "@/lib/format";
import FollowerSourceBreakdown from "@/components/FollowerSourceBreakdown";

const FOLLOWER_ROLES = new Set(["artist_owner", "artist_team", "admin"]);

export default function Audience() {
    const { activeArtistId, role } = useRole();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!activeArtistId) return;
        setLoading(true);
        endpoints.audience(activeArtistId).then((d) => {
            setData(d);
            setLoading(false);
        });
    }, [activeArtistId]);

    if (loading) return <div className="p-6 md:p-10 font-mono text-sm text-[#666]" data-testid="audience-loading">Loading audience…</div>;
    if (!data) return null;

    const maxCity = Math.max(1, ...data.by_city.map((c) => c.count));
    const tl = data.follower_timeline;
    const canSeeFollowers = FOLLOWER_ROLES.has(role);

    return (
        <div className="pm-fade" data-testid="audience-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Audience</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Who's listening</h1>
            </header>

            <section className="grid grid-cols-3 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                <Metric icon={Users} label="Total unique fans" value={data.total_fans.toLocaleString()} testId="aud-total" />
                <Metric icon={Repeat} label="Returning fans" value={data.returning_fans.toLocaleString()} testId="aud-returning" />
                <Metric icon={UserPlus} label="New fans" value={data.new_fans.toLocaleString()} testId="aud-new" />
            </section>

            {canSeeFollowers && tl && (
                <>
                    <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]" data-testid="aud-follower-metrics">
                        <Metric icon={Heart} label="Current followers" value={tl.current_followers.toLocaleString()} testId="follower-current" />
                        <Metric icon={UserPlus} label="New · last 7 days" value={tl.new_last_7_days.toLocaleString()} testId="follower-7d" />
                        <Metric icon={UserPlus} label="New · last 30 days" value={tl.new_last_30_days.toLocaleString()} testId="follower-30d" />
                        <Metric icon={Sparkles} label="Best event window" value={(tl.top_events_by_growth[0]?.window_total ?? 0).toLocaleString()} testId="follower-best-event" sub={tl.top_events_by_growth[0]?.event_name || "—"} />
                    </section>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                        <div className="lg:col-span-2 bg-[#0a0a0a] p-6" data-testid="aud-follower-timeline">
                            <div className="text-[10px] font-mono uppercase text-[#666] flex items-center gap-1"><TrendingUp className="w-3 h-3" /> {tl.timeline_days} days</div>
                            <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-2">Follower growth</h2>
                            <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mb-4">
                                {tl.legacy_followers > 0 && <>Legacy follows excluded from growth ({tl.legacy_followers} legacy)</>}
                            </div>
                            <div className="h-56" style={{ minWidth: 1, minHeight: 1 }}>
                                <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                                    <AreaChart data={tl.timeline} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                                        <defs>
                                            <linearGradient id="pmFollowerGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="#ff4400" stopOpacity={0.5} />
                                                <stop offset="100%" stopColor="#ff4400" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 9, fontFamily: "monospace" }} tickFormatter={(d) => d.slice(5)} interval={Math.max(0, Math.floor(tl.timeline.length / 6))} stroke="#2a2a2a" />
                                        <YAxis tick={{ fill: "#666", fontSize: 10, fontFamily: "monospace" }} allowDecimals={false} stroke="#2a2a2a" width={24} />
                                        <Tooltip contentStyle={{ background: "#141414", border: "1px solid #2a2a2a", fontSize: 12, fontFamily: "monospace", textTransform: "uppercase" }} labelStyle={{ color: "#a3a3a3" }} itemStyle={{ color: "#ff4400" }} />
                                        <Area type="monotone" dataKey="count" stroke="#ff4400" strokeWidth={2} fill="url(#pmFollowerGrad)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                            {tl.top_events_by_growth.length > 0 && (
                                <div className="mt-4">
                                    <div className="text-[10px] font-mono uppercase text-[#666] mb-2">Top events by follower growth</div>
                                    <div className="border border-[#2a2a2a]">
                                        {tl.top_events_by_growth.map((e) => (
                                            <Link
                                                to={`/workspace/events/${e.event_id}/performance`}
                                                key={e.event_id}
                                                className="flex items-center gap-3 p-3 border-b border-[#2a2a2a] last:border-b-0 hover:bg-[#141414]"
                                                data-testid={`follower-top-event-${e.event_id}`}
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-display font-bold uppercase text-sm truncate">{e.event_name}</div>
                                                    <div className="text-[10px] font-mono uppercase text-[#666]">{formatDate(e.event_date)}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-sm font-display font-bold text-[#ff4400]">+{e.window_total}</div>
                                                    <div className="text-[10px] font-mono uppercase text-[#a3a3a3]">{e.direct_event_follows} direct</div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-[#0a0a0a] p-6" data-testid="aud-recent-follows">
                            <div className="text-[10px] font-mono uppercase text-[#666]">Latest follows</div>
                            <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-4">Recent activity</h2>
                            {tl.recent_follows.length === 0 ? (
                                <div className="text-sm text-[#a3a3a3]" data-testid="follower-recent-empty">No recorded follows yet. Legacy follows exist but have no timestamp.</div>
                            ) : (
                                <div className="border border-[#2a2a2a]">
                                    {tl.recent_follows.map((f) => (
                                        <div key={f.id} className="p-3 border-b border-[#2a2a2a] last:border-b-0 flex items-center gap-3" data-testid={`follower-recent-${f.id}`}>
                                            {f.fan_image_url ? (
                                                <img src={f.fan_image_url} alt="" className="w-8 h-8 object-cover grayscale" />
                                            ) : (
                                                <div className="w-8 h-8 bg-[#141414] border border-[#2a2a2a] flex items-center justify-center text-[10px] font-mono uppercase">{(f.fan_name || "?").charAt(0)}</div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-display font-bold uppercase truncate">{f.fan_name}</div>
                                                <div className="text-[10px] font-mono uppercase text-[#666] truncate">
                                                    {f.source || "unknown"}{f.source_event_name ? ` · ${f.source_event_name}` : ""}
                                                </div>
                                            </div>
                                            <div className="text-[10px] font-mono uppercase text-[#a3a3a3] whitespace-nowrap">
                                                {formatDate(f.followed_at)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <FollowerSourceBreakdown artistId={activeArtistId} />
                </>
            )}

            {!canSeeFollowers && (
                <div className="px-4 md:px-8 py-4 border-b border-[#2a2a2a] flex items-start gap-2 text-xs font-mono uppercase text-[#666]" data-testid="follower-analytics-restricted">
                    <ShieldAlert className="w-4 h-4 text-[#666] mt-0.5" />
                    Follower analytics are restricted to Artist Owner, Team Member, and Admin roles.
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#2a2a2a]">
                <div className="bg-[#0a0a0a] p-6">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Where they're from</div>
                    <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-4">Cities</h2>
                    {data.by_city.length === 0 ? (
                        <div className="text-sm text-[#a3a3a3]">No audience data yet.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.by_city.map((c) => (
                                <div key={c.city} data-testid={`aud-city-${c.city}`}>
                                    <div className="flex items-center justify-between text-xs font-mono uppercase">
                                        <span>{c.city}</span>
                                        <span className="text-[#a3a3a3]">{c.count.toLocaleString()}</span>
                                    </div>
                                    <div className="h-2 bg-[#141414] mt-1"><div className="h-full bg-[#ff4400]" style={{ width: `${(c.count / maxCity) * 100}%` }} /></div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-[#0a0a0a] p-6">
                    <div className="text-[10px] font-mono uppercase text-[#666]">Latest signups</div>
                    <h2 className="mt-1 font-display font-black uppercase text-xl md:text-2xl mb-4">Recent registrations</h2>
                    <div className="border border-[#2a2a2a]">
                        {data.recent_registrations.length === 0 ? (
                            <div className="p-4 text-sm text-[#a3a3a3]">Nothing yet.</div>
                        ) : data.recent_registrations.map((r) => (
                            <div key={r.id} className="p-3 border-b border-[#2a2a2a] last:border-b-0 flex items-center gap-3" data-testid={`aud-recent-${r.id}`}>
                                <div className="w-8 h-8 bg-[#141414] border border-[#2a2a2a] flex items-center justify-center text-[10px] font-mono uppercase">{r.fan_name.charAt(0)}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-display font-bold uppercase truncate">{r.fan_name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666] truncate">{r.fan_email}</div>
                                </div>
                                <div className={`text-[10px] font-mono uppercase ${r.checked_in ? "text-[#00e676]" : "text-[#a3a3a3]"}`}>
                                    {r.checked_in ? "checked in" : "registered"}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Metric({ icon: Icon, label, value, testId, sub }) {
    return (
        <div className="bg-[#0a0a0a] p-5" data-testid={testId}>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#666]">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="mt-2 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">{value}</div>
            {sub && <div className="text-[10px] font-mono uppercase text-[#a3a3a3] mt-1 truncate">{sub}</div>}
        </div>
    );
}
